import type { InvitationChannelMode } from '@/types/organization';

/**
 * Copy for the customer sign-in "identity" field, driven by a child org's
 * `invitation_channel_mode`. A client that only invites by email should never
 * be prompted for a mobile number (and vice versa), so the label, placeholder
 * and surrounding prompt all follow the configured channel.
 *
 * The identity field itself always accepts either an email or a phone number
 * at submit time; this only tailors the *copy* to the org's channel so it
 * matches what customers were actually invited with.
 */
export interface IdentityFieldCopy {
  /** Field label, eg "Email Address" or "Email Address or Mobile Number". */
  label: string;
  /** Input placeholder example matching the label. */
  placeholder: string;
  /** Lower-case noun phrase for use inside sentences, eg "mobile number". */
  prompt: string;
}

const EMAIL_ONLY: IdentityFieldCopy = {
  label: 'Email Address',
  placeholder: 'you@example.com',
  prompt: 'email address',
};

const SMS_ONLY: IdentityFieldCopy = {
  label: 'Mobile Number',
  placeholder: '07700 900123',
  prompt: 'mobile number',
};

const EMAIL_OR_SMS: IdentityFieldCopy = {
  label: 'Email Address or Mobile Number',
  placeholder: 'you@example.com or 07700 900123',
  prompt: 'email address or mobile number',
};

/**
 * Resolve the identity-field copy for a channel mode. Unknown / missing modes
 * fall back to `email_only`, matching the platform-wide default used by the
 * invitation wizard and Integrations settings.
 */
export function getIdentityFieldCopy(
  mode: InvitationChannelMode | null | undefined,
): IdentityFieldCopy {
  switch (mode) {
    case 'sms_only':
      return SMS_ONLY;
    case 'email_or_sms':
      return EMAIL_OR_SMS;
    case 'email_only':
    default:
      return EMAIL_ONLY;
  }
}

/**
 * The channel a raw identifier string looks like, used to tailor journey copy
 * (button label, success heading) before the send response is known. Pure and
 * client-safe: contains '@' → email; a phone-shaped string (≥7 digits, only
 * phone characters) → sms; anything else, including empty → unknown.
 */
export type DetectedChannel = 'email' | 'sms' | 'unknown';

export function detectIdentifierChannel(
  identifier: string | null | undefined,
): DetectedChannel {
  const value = (identifier ?? '').trim();
  if (!value) return 'unknown';
  if (value.includes('@')) return 'email';
  const digits = value.replace(/[^0-9]/g, '');
  if (digits.length >= 7 && /^[+0-9 ()-]+$/.test(value)) return 'sms';
  return 'unknown';
}

/**
 * Channel-aware copy for the customer sign-in journey (send-button label,
 * "receive …" phrase, success heading). Combines the org's invitation channel
 * mode with the *detected* identifier so a mobile number never shows magic-link
 * wording and vice versa. When the identifier is ambiguous the org mode decides;
 * a combined org with nothing typed yet uses neutral "sign-in code" wording.
 */
export interface AuthJourneyCopy {
  /** Send-button label, eg "Send Magic Link" or "Text me a code". */
  buttonLabel: string;
  /** Trailing noun phrase for "…to receive {receivePhrase}". */
  receivePhrase: string;
  /** Success screen heading once the link/code is sent. */
  successHeading: string;
}

export function getAuthJourneyCopy(
  mode: InvitationChannelMode | null | undefined,
  identifier?: string | null,
): AuthJourneyCopy {
  const detected = detectIdentifierChannel(identifier);
  // When the identifier is ambiguous, mirror getIdentityFieldCopy's fallback:
  // only an explicit email_or_sms org is "combined"; sms_only → sms; everything
  // else (email_only, null, unknown mode) → email.
  const effective: DetectedChannel =
    detected !== 'unknown'
      ? detected
      : mode === 'sms_only'
        ? 'sms'
        : mode === 'email_or_sms'
          ? 'unknown'
          : 'email';

  switch (effective) {
    case 'email':
      return {
        buttonLabel: 'Send Magic Link',
        receivePhrase: 'a magic link',
        successHeading: 'Check Your Email!',
      };
    case 'sms':
      return {
        buttonLabel: 'Text me a code',
        receivePhrase: 'a 6-digit code',
        successHeading: 'Check Your Messages!',
      };
    default:
      return {
        buttonLabel: 'Send sign-in code',
        receivePhrase: 'a sign-in link or code',
        successHeading: 'Check Your Messages!',
      };
  }
}
